import { useQuery } from '@tanstack/react-query'
import { isUsersApiEnabled, USERS_API_URL } from 'utils/constants'
import { fromUnixTime, milliseconds } from 'date-fns'
import { useWallet } from '@solana/wallet-adapter-react'
import { toContextTuplesString } from 'utils/logging'

interface ProfileDetailsFromServer {
  wallet: string
  username: string
  hasImage: boolean
  partnerStartAtUnixTime: number | null
}

export type ProfileDetails = Omit<
  ProfileDetailsFromServer,
  'partnerStartAtUnixTime'
> & {
  partnerStartAt: Date | null
}

export async function fetchProfileDetails(
  walletPubkeyStr: string,
): Promise<ProfileDetailsFromServer | null> {
  if (!isUsersApiEnabled()) return null
  try {
    const profileResp = await fetch(`${USERS_API_URL}/user/${walletPubkeyStr}`)

    if (!profileResp.ok) {
      if (profileResp.status !== 404) {
        console.error(
          `error fetching profile details: ${toContextTuplesString({
            status: profileResp.status,
            body: await profileResp.text(),
            wallet: walletPubkeyStr,
          })}`,
        )
      }
      return null
    }

    const data = await profileResp.json()

    return data as ProfileDetailsFromServer
  } catch (error) {
    console.error(
      `error fetching profile details: wallet=${walletPubkeyStr}`,
      error,
    )
    return null
  }
}

export function useProfileDetails(wallet?: string | null) {
  const { publicKey } = useWallet()
  const walletPubkeyStr = wallet ? wallet : publicKey?.toBase58()

  const {
    data: profileDetails,
    isInitialLoading,
    isFetching,
    refetch,
  } = useQuery<ProfileDetails | null>({
    queryKey: ['getProfileDetails', walletPubkeyStr],
    queryFn: async () => {
      if (!walletPubkeyStr) {
        throw new Error('Wallet pubkey is not available')
      }

      const profileDetails = await fetchProfileDetails(walletPubkeyStr)

      if (!profileDetails) {
        return null
      }
      const { partnerStartAtUnixTime, ...otherProps } = profileDetails
      return {
        ...otherProps,
        partnerStartAt:
          partnerStartAtUnixTime != null
            ? fromUnixTime(partnerStartAtUnixTime)
            : null,
      }
    },
    enabled: !!walletPubkeyStr,
    staleTime: milliseconds({ hours: 10 }),
    retry: 3,
  })

  return {
    profileDetails,
    loadingProfileDetails: isInitialLoading,
    isFetchingProfileDetails: isFetching,
    refetchProfileDetails: refetch,
  }
}
